import { useEffect } from 'react';

import { useSegments } from 'expo-router';

import { RunningExperiments, useExperiments } from 'state/experiments';

const TREATMENT_VARIANT = 'treatment';

const ROUTE_GROUP_SEGMENT = /^\(.+\)$/;

/**
 * File-route templates (useSegments, groups/`index` stripped) that render the
 * experiment. Dynamic folders keep their `[param]` name, so `/rewards/:id`
 * (`rewards/[rewardId]`) is distinct from `/rewards/profile`.
 */
const EXPOSURE_ROUTE_TEMPLATES = new Set([
  'offers',
  'offers/[loyaltyEngineId]',
  'offers/group/[offerGroupId]',
  'rewards',
  'rewards/[rewardId]',
]);

const toRouteTemplate = (segments: readonly string[]) =>
  segments
    .filter(
      segment => Boolean(segment) && !ROUTE_GROUP_SEGMENT.test(segment) && segment !== 'index'
    )
    .join('/');

/**
 * True when Expo Router's current file segments are an offers/rewards
 * list or detail surface. Home, cart, store-locator, and static rewards
 * tabs (`profile`, `history`, …) do not match.
 */
export const isRenderReadyOffersRewardsExposureRoute = (segments: readonly string[]): boolean =>
  EXPOSURE_ROUTE_TEMPLATES.has(toRouteTemplate(segments));

/**
 * Reads the render-ready offers/rewards experiment variant WITHOUT logging an
 * exposure. Exposure is logged separately, and only for eligible users, via
 * {@link useExposeRenderReadyOffersRewardsExperiment}. This keeps the experiment
 * safe by default: if Amplitude is slow or fails, this returns `false` and
 * callers fall back to the legacy path.
 */
export const useRenderReadyOffersRewardsEnabled = () => {
  const { variant } = useExperiments();

  return variant(RunningExperiments.EnableRenderReadyOffersRewards, TREATMENT_VARIANT, {
    skipExposureLogging: true,
  });
};

/**
 * Logs an Amplitude exposure for the render-ready offers/rewards experiment.
 *
 * Pass `shouldExpose = true` only when the user is actually eligible to
 * experience a difference between arms (kill-switch on + unified menu, plus a
 * selected store where relevant). This must be true for BOTH control and
 * treatment so exposure is recorded for the whole eligible population.
 * Visibility is the current Expo Router file segments: only offers/rewards
 * list and detail routes count. Child-tab focus is not used — NativeTabs can
 * keep a tab "focused" under cart or store-locator.
 * `logExposure` dedupes per variant, so calling it from multiple surfaces is safe.
 */
export const useExposeRenderReadyOffersRewardsExperiment = (shouldExpose: boolean) => {
  const { logExposure } = useExperiments();
  const segments = useSegments();
  const isExperimentSurface = isRenderReadyOffersRewardsExposureRoute(segments);

  useEffect(() => {
    if (!shouldExpose || !isExperimentSurface) {
      return;
    }

    logExposure(RunningExperiments.EnableRenderReadyOffersRewards);
  }, [shouldExpose, isExperimentSurface, logExposure]);
};
